import {
  HomeIcon,
  PersonIcon,
  AlertIcon,
  TasklistIcon,
  QuestionBubbleIcon,
  FilterIcon,
  SpaceIcon,
  ToolsIcon,
} from '@primer/octicons-react'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {SoftNavOnlyLink} from '@github-ui/react-router'
import {Button, CounterLabel, NavList} from '@primer/react'
import {SkeletonAvatar, SkeletonText} from '@primer/react/experimental'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {clsx} from 'clsx'
import {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import {sendEvent} from '@github-ui/hydro-analytics'
import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {UITask} from '../../types/task'
import type {SidebarChat} from '../../types/sidebar-chat'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import {useQueryClient} from '@github-ui/react-query'
import {agentGroupedTasksQueryKey} from '../../hooks/use-grouped-tasks-data'
import {sidebarRepositoryQueryKey} from '../../hooks/use-sidebar-repository-filter'
import type {SidebarRepository} from '../../types/sidebar-repository'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'
import {ChatsNavGroup, type AgentsChatsState} from './ChatsNavGroup'
import {
  SessionsByRepoNav,
  hasRenderableGroups,
  type RepositoryAgentEnabledById,
} from './SessionsByRepoNav/SessionsByRepoNav'
import {
  groupTasksByRepo,
  groupSessionsByStatus,
  groupSessionsByEnvironment,
  groupSessionsByRecency,
  chatsWithoutMatchingTasks,
  sortGroupableItemsByDate,
  type TaskRepoGroup,
  type SidebarGroup,
} from '../../hooks/use-grouped-tasks'
import {NEEDS_ATTENTION_QUERY, getCreatedByMeSearchParams} from './queries'
import {CANONICAL_AGENTS_CUSTOMIZE_PATH, getAgentsPath, getAgentsSpacesPath} from '../../utils/constants'
import {isQuickChatTask} from '../../utils/is-quick-chat-task'
import {shouldUseGlobalTaskUrls} from '../../utils/generate-task-path'
import {
  clearSidebarFilters,
  FILTER_STATUS_NEEDS_ATTENTION,
  getEffectiveSidebarFilters,
  LEGACY_FILTER_STATUS_NEEDS_ATTENTION,
  hasActiveSidebarFilter,
  GROUP_BY_STATUS,
  GROUP_BY_LAST_UPDATED,
  GROUP_BY_ENVIRONMENT,
  isNonRepositoryGrouping,
  type SidebarGroupBy,
  type SidebarParsedFilters,
} from '../../utils/filter-utils'
import {matchesSidebarChatFilters} from '../../utils/sidebar-chat-filters'
import {readSidebarFiltersFromSearchParams, writeSidebarFiltersToSearchParams} from '../../utils/session-filter-url'
import {SidebarFilterMenu} from './SidebarFilterMenu'
import {ArchiveErrorBanner} from '../ArchiveErrorBanner'
import {TaskActionProviders} from '../task-list/TaskActionProviders'
import styles from './AgentsSidebar.module.css'
import stateStyles from './SidebarState.module.css'
import {useLegacySidebarChats} from '../../hooks/use-legacy-sidebar-chats'
import {useSidebarGroupBy} from '../../hooks/use-sidebar-group-by'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {excludePinnedSidebarRows} from '../../utils/task-pins'
import {PinnedSessions, type PinnedSessionsState} from './PinnedSessions'
import {useTaskPinFocus} from '../../hooks/use-task-pin-focus'

export interface AgentsSidebarProps {
  pins?: PinnedSessionsState
  currentUserLogin: string | undefined
  /** ID of the task currently being viewed; highlights its row and clears the top-level tab selection. */
  activeTaskId?: string
  /** Tasks to render under the Sessions section, grouped by repo client-side. Ignored when `groups` is provided. */
  tasks?: readonly UITask[]
  /** Pre-grouped tasks from the server; when provided, renders these instead of grouping `tasks` client-side. */
  groups?: readonly TaskRepoGroup[]
  /** Dotcom-owned repository policy snapshot, keyed by repository database ID. */
  repositoryAgentEnabledById?: RepositoryAgentEnabledById
  /** Whether the tasks query is still loading; keeps the shared sidebar skeleton visible until all sources are ready. */
  isLoadingTasks?: boolean
  /** Whether the tasks query failed; shows an inline error instead of the list. */
  isErrorTasks?: boolean
  /** Fatal sidebar facet resolution failure. Hides all rows and shows the sessions error. */
  facetResolutionError?: boolean
  sidebarRepository?: SidebarRepository
  repositoryNotFound?: boolean
  filterableAgents?: AgentInfo[]
  onNavigateHome?: () => void
  /** Called when the new session (+) button is clicked, even when already on Home. Used to dismiss the narrow-viewport sidebar dialog. */
  onPlusButtonClick?: () => void
  /** Number of the current user's sessions waiting for input; renders a badge on the Needs attention tab. */
  needsAttentionCount?: number
  /** When true, sidebar actions return to `/copilot/agents` instead of only updating the current URL's params. */
  navigateToAgentsPage?: boolean
  /** Whether to include legacy v1 Chat history in the sidebar. */
  showLegacyChatHistory?: boolean
  legacyChatHistoryPending?: boolean
  legacyChatHistoryFailed?: boolean
  onSidebarStateChange?: (
    sidebarId: string,
    state?: {isLoading: boolean; isError: boolean; isFilteredEmpty: boolean; isRepositoryNotFound: boolean},
  ) => void
  /** ID of the legacy v1 Chat displayed by the current page. */
  activeThreadId?: string
  /** Task ID that should trigger redirect to list after deletion from sidebar actions. */
  activeTaskIdForDeleteRedirect?: string
  /** Chat history state to render as the first nested group under Sessions. */
  agentsChats?: AgentsChatsState
  /** API origin used to load Chat rows; keeps hovercard detail requests on the same deployment. */
  chatApiURL?: string
  canCreateChat?: boolean
  /** Identity of the displayed conversation; selects its row in the Chats group and must never be gated on renderer choice. */
  activeChatTaskId?: string
  getChatStatusLabel?: (chat: SidebarChat) => string
  /** Called after a normal chat link or new-chat action is soft-navigated. */
  onChatNavigate?: () => void
  /** Whether the current page is the admin-only Spaces list or detail route; highlights the Spaces tab. */
  isSpacesActive?: boolean
  /** Whether to render the Spaces nav item at all. */
  showSpacesNav?: boolean
  /** Whether the current page is the Customize route; highlights the Customize tab. */
  isCustomizeActive?: boolean
  /** Whether to render the Customize nav item at all. */
  showCustomizeNav?: boolean
}

export type AgentsSidebarView = 'home' | 'created_by_me' | 'needs_attention'

// When filtering is disabled, ignore `sq` so URL parameters cannot change grouping or filters.
const DEFAULT_SIDEBAR_FILTERS: SidebarParsedFilters = {
  status: 'all',
  detailedStatuses: [],
  creators: [],
  excludedCreators: [],
  agents: [],
  types: [],
  sort: 'newest',
  freeText: '',
}

const FAILED_LEGACY_CHATS: ReturnType<typeof useLegacySidebarChats> = {chats: [], isLoading: false, isError: true}
const PENDING_LEGACY_CHATS: ReturnType<typeof useLegacySidebarChats> = {chats: [], isLoading: true, isError: false}

function mergeSidebarChats(
  agentsChats: AgentsChatsState | undefined,
  legacy: ReturnType<typeof useLegacySidebarChats>,
  legacyEnabled: boolean,
  applyWatermark: boolean,
  activeThreadId?: string,
): AgentsChatsState | undefined {
  if (!agentsChats && !legacyEnabled) return undefined
  const v2Chats = agentsChats && !agentsChats.isErrorChats ? agentsChats.chats : []
  const oldestLoaded =
    applyWatermark && agentsChats?.hasMoreChats && v2Chats.length
      ? Math.min(...v2Chats.map(chat => Date.parse(chat.updatedAt)).filter(Number.isFinite))
      : undefined
  const legacyChats =
    oldestLoaded !== undefined && Number.isFinite(oldestLoaded)
      ? legacy.chats.filter(chat => chat.taskId === activeThreadId || Date.parse(chat.updatedAt) >= oldestLoaded)
      : legacy.chats
  const chats = sortGroupableItemsByDate([
    ...v2Chats.map(chat => ({kind: 'chat' as const, chat})),
    ...legacyChats.map(chat => ({kind: 'chat' as const, chat})),
  ]).flatMap(item => (item.kind === 'chat' ? [item.chat] : []))
  const isLoadingChats = Boolean(agentsChats?.isLoadingChats) || (chats.length === 0 && legacy.isLoading)
  const sourceFailed = Boolean(agentsChats?.isErrorChats || legacy.isError)
  const allSourcesFailed = (!agentsChats || agentsChats.isErrorChats) && (!legacyEnabled || legacy.isError)
  const isErrorChats =
    sourceFailed && allSourcesFailed && !legacy.isLoading && (!agentsChats?.isLoadingChats || agentsChats.isErrorChats)

  return {
    ...agentsChats,
    chats,
    isLoadingChats,
    isErrorChats,
    partialErrorMessage: sourceFailed && !allSourcesFailed ? 'Couldn’t load some chats' : undefined,
  }
}

function CollapsedSessionsIcon(props: {size?: number; className?: string}) {
  const {isExpanded} = useCollapsibleSidebarContext()
  return isExpanded ? null : <TasklistIcon {...props} />
}

/**
 * Determines the active sidebar view from current URL search params.
 * Any presence of ?q= or filter-related params means we're in a task list view.
 */
export function getActiveView(searchParams: URLSearchParams, currentUserLogin: string | undefined): AgentsSidebarView {
  const qParam = searchParams.get('q')

  if (qParam !== null) {
    // Parse the q param to determine which sidebar tab is active
    // Accept both kebab-case and legacy snake_case for backwards compat
    if (
      qParam.includes(`status:${FILTER_STATUS_NEEDS_ATTENTION}`) ||
      qParam.includes(`status:${LEGACY_FILTER_STATUS_NEEDS_ATTENTION}`)
    )
      return 'needs_attention'
    if (qParam.includes('author:')) return 'created_by_me'
    // Any other q= value means user is filtering in the task list
    return 'created_by_me'
  }

  // Legacy fallback: check individual params
  const status = searchParams.get('status')
  const author = searchParams.get('author')
  if (status === FILTER_STATUS_NEEDS_ATTENTION || status === LEGACY_FILTER_STATUS_NEEDS_ATTENTION)
    return 'needs_attention'
  if (author === currentUserLogin && currentUserLogin) return 'created_by_me'
  if (author || status) return 'created_by_me'

  return 'home'
}

/**
 * Sidebar navigation for the /copilot/agents page.
 * Tabs: Home, My sessions, Needs attention.
 * Active tab is derived from the `?q=` param content (e.g. `author:@me` → My sessions).
 */
export function AgentsSidebar({
  currentUserLogin,
  activeTaskId,
  tasks: allTasks,
  groups: allGroups,
  repositoryAgentEnabledById,
  isLoadingTasks,
  isErrorTasks,
  facetResolutionError,
  sidebarRepository,
  repositoryNotFound = false,
  filterableAgents,
  onNavigateHome,
  onPlusButtonClick,
  needsAttentionCount,
  navigateToAgentsPage = false,
  showLegacyChatHistory = false,
  legacyChatHistoryPending = false,
  legacyChatHistoryFailed = false,
  onSidebarStateChange,
  activeThreadId,
  activeTaskIdForDeleteRedirect,
  agentsChats,
  chatApiURL,
  canCreateChat,
  activeChatTaskId,
  getChatStatusLabel,
  onChatNavigate,
  isSpacesActive = false,
  showSpacesNav = false,
  isCustomizeActive = false,
  showCustomizeNav = false,
  pins,
}: AgentsSidebarProps) {
  const shownPins = copilotFeatureFlags.taskPinning ? pins : undefined
  const pinnedIds = useMemo(
    () => new Set(shownPins?.tasks.flatMap(task => (task.id ? [task.id] : [])) ?? []),
    [shownPins?.tasks],
  )
  const {tasks, groups} = useMemo(
    () => excludePinnedSidebarRows(allTasks, allGroups, undefined, pinnedIds),
    [allTasks, allGroups, pinnedIds],
  )
  const agentsBasePath = getAgentsPath()
  const agentsSpacesPath = getAgentsSpacesPath()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const {isExpanded: isSidebarExpanded} = useCollapsibleSidebarContext()
  // `getActiveView` falls back to Home, so routes that highlight their own row opt out.
  const activeView =
    activeTaskId || isSpacesActive || isCustomizeActive ? undefined : getActiveView(searchParams, currentUserLogin)
  const needsAttentionCountDisplay = needsAttentionCount && needsAttentionCount > 99 ? '99+' : needsAttentionCount
  const showSidebarFiltering = isFeatureEnabled('copilot_mission_control_sidebar_filtering')
  const showSidebarFacetFiltering =
    showSidebarFiltering && isFeatureEnabled('copilot_mission_control_sidebar_facet_filters')
  const showSidebarTypeFacet =
    showSidebarFacetFiltering && isFeatureEnabled('copilot_mission_control_sidebar_type_filter')
  const useGlobalTaskUrls = shouldUseGlobalTaskUrls({
    taskUrlScope: 'global',
    taskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_task_detail'),
    repositoryTaskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_repository_task_detail'),
  })
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false)
  const filterButtonRef = useRef<HTMLButtonElement>(null)
  const sidebarContainerRef = useRef<HTMLDivElement>(null)
  const capturePinFocus = useTaskPinFocus(sidebarContainerRef, !!shownPins)
  const restoreResetFocusRef = useRef(false)
  const olderChatsFocusRef = useRef<{button: HTMLButtonElement; chatCount: number; startedLoading: boolean} | null>(
    null,
  )
  const wasFilteredEmptyRef = useRef(false)
  const wasRepositoryNotFoundRef = useRef(false)
  const sidebarQuery = searchParams.get('sq')
  // The repo: filter works even with the flag off; every other filter/grouping stays gated.
  const parsedSidebarFilters = useMemo(
    () =>
      readSidebarFiltersFromSearchParams(new URLSearchParams({sq: sidebarQuery ?? ''}), currentUserLogin, {
        includeFacets: showSidebarFacetFiltering,
        includeTypeFacet: showSidebarTypeFacet,
      }),
    [showSidebarFacetFiltering, showSidebarTypeFacet, sidebarQuery, currentUserLogin],
  )
  const sidebarFilters = useMemo(
    () => (showSidebarFiltering ? parsedSidebarFilters : {...DEFAULT_SIDEBAR_FILTERS, repo: parsedSidebarFilters.repo}),
    [showSidebarFiltering, parsedSidebarFilters],
  )
  const hasActiveFilter = hasActiveSidebarFilter(sidebarFilters, {
    includeFacets: showSidebarFacetFiltering,
    includeTypeFacet: showSidebarTypeFacet,
  })
  const showRepositoryNotFound = repositoryNotFound && hasActiveFilter
  const effectiveSidebarFilters = useMemo(
    () =>
      getEffectiveSidebarFilters(sidebarFilters, {
        includeFacets: showSidebarFacetFiltering,
        includeTypeFacet: showSidebarTypeFacet,
      }),
    [showSidebarFacetFiltering, showSidebarTypeFacet, sidebarFilters],
  )
  const {groupBy: effectiveGroupBy, persistGroupBy} = useSidebarGroupBy({
    urlGroupBy: parsedSidebarFilters.groupBy,
    currentUserLogin,
    filteringEnabled: showSidebarFiltering,
  })

  const handleGroupByChange = (groupBy: SidebarGroupBy | undefined) => {
    if (!showSidebarFiltering) return
    persistGroupBy(groupBy ?? GROUP_BY_LAST_UPDATED)
    setSearchParams(prev =>
      writeSidebarFiltersToSearchParams(
        {...sidebarFilters, groupBy: groupBy === GROUP_BY_LAST_UPDATED ? undefined : groupBy},
        prev,
        currentUserLogin,
      ),
    )
  }
  const handleStatusChange = (detailedStatuses: string[]) => {
    if (!showSidebarFiltering) return
    setSearchParams(prev =>
      writeSidebarFiltersToSearchParams({...sidebarFilters, detailedStatuses}, prev, currentUserLogin),
    )
  }
  const handleFacetChange = (facet: 'types' | 'agents', values: string[]) => {
    if (!showSidebarFacetFiltering || (facet === 'types' && !showSidebarTypeFacet)) return
    setSearchParams(prev =>
      writeSidebarFiltersToSearchParams({...sidebarFilters, [facet]: values}, prev, currentUserLogin),
    )
  }
  const queryClient = useQueryClient()
  const handleRepositoryChange = (repo: AgentDropdownRepository | undefined) => {
    if (!showSidebarFiltering) return
    if (repo?.id && repo.id > 0) {
      queryClient.setQueryData<SidebarRepository>(sidebarRepositoryQueryKey(repo.nameWithOwner), {
        id: repo.id,
        nameWithOwner: repo.nameWithOwner,
      })
    }
    setSearchParams(prev =>
      writeSidebarFiltersToSearchParams({...sidebarFilters, repo: repo?.nameWithOwner}, prev, currentUserLogin),
    )
  }
  const handleResetFilters = () => {
    if (!showSidebarFiltering) return
    setSearchParams(prev =>
      writeSidebarFiltersToSearchParams(
        clearSidebarFilters(sidebarFilters, {
          includeFacets: showSidebarFacetFiltering,
          includeTypeFacet: showSidebarTypeFacet,
        }),
        prev,
        currentUserLogin,
      ),
    )
  }
  const handleResetEmptyFilters = () => {
    restoreResetFocusRef.current = true
    handleResetFilters()
  }

  const isMergedGroupingMode = isNonRepositoryGrouping(effectiveGroupBy)
  const loadedLegacy = useLegacySidebarChats(showLegacyChatHistory)
  const legacy = legacyChatHistoryFailed
    ? FAILED_LEGACY_CHATS
    : legacyChatHistoryPending
      ? PENDING_LEGACY_CHATS
      : loadedLegacy
  const legacyChatHistoryEnabled = showLegacyChatHistory || legacyChatHistoryFailed || legacyChatHistoryPending
  const sidebarId = useId()
  const mergedSidebarChats = useMemo(() => {
    const merged = mergeSidebarChats(
      agentsChats,
      legacy,
      legacyChatHistoryEnabled,
      !isMergedGroupingMode,
      activeThreadId,
    )
    return merged
      ? {...merged, chats: excludePinnedSidebarRows(undefined, undefined, merged.chats, pinnedIds).chats ?? []}
      : undefined
  }, [agentsChats, legacy, legacyChatHistoryEnabled, isMergedGroupingMode, activeThreadId, pinnedIds])
  const sidebarChats = useMemo(
    () =>
      facetResolutionError
        ? undefined
        : showSidebarFiltering && mergedSidebarChats && hasActiveFilter
          ? {
              ...mergedSidebarChats,
              chats: mergedSidebarChats.chats.filter(chat =>
                matchesSidebarChatFilters(chat, effectiveSidebarFilters, sidebarRepository, filterableAgents),
              ),
            }
          : mergedSidebarChats,
    [
      showSidebarFiltering,
      mergedSidebarChats,
      effectiveSidebarFilters,
      hasActiveFilter,
      sidebarRepository,
      filterableAgents,
      facetResolutionError,
    ],
  )
  const effectiveGroups = useMemo<readonly TaskRepoGroup[] | readonly SidebarGroup[] | undefined>(() => {
    if (!isMergedGroupingMode) return groups
    const baseGroups = groups ?? groupTasksByRepo(tasks ?? [])
    // Chats only merge into non-repository groupings; Repository grouping keeps
    // chats in their own pinned section (see ChatsNavGroup usage below).
    const chats = sidebarChats?.chats ?? []
    if (effectiveGroupBy === GROUP_BY_STATUS) return groupSessionsByStatus(baseGroups, chats)
    if (effectiveGroupBy === GROUP_BY_ENVIRONMENT) return groupSessionsByEnvironment(baseGroups, chats)
    return groupSessionsByRecency(baseGroups, chats)
  }, [groups, tasks, effectiveGroupBy, isMergedGroupingMode, sidebarChats])

  const repositoryAgentsChats = useMemo(() => {
    if (isMergedGroupingMode || !sidebarChats) return sidebarChats
    const renderedTasks = groups?.flatMap(([, groupTasks]) => groupTasks) ?? tasks ?? []
    const chats = chatsWithoutMatchingTasks(renderedTasks, sidebarChats.chats)
    return chats.length === sidebarChats.chats.length ? sidebarChats : {...sidebarChats, chats}
  }, [sidebarChats, groups, isMergedGroupingMode, tasks])

  // Navigates to /copilot/agents when off-page, else just updates the current URL's params.
  const applyTabParams = (params: URLSearchParams) => {
    if (navigateToAgentsPage) {
      const search = params.toString()
      navigate(search ? `${agentsBasePath}?${search}` : agentsBasePath)
    } else {
      setSearchParams(params)
    }
  }

  const navigateToHome = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault()
    sendEvent('agent_sessions.sidebar_tab_click', {target: 'HOME'})
    applyTabParams(new URLSearchParams())
    onNavigateHome?.()
  }

  const navigateToCreatedByMe = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    sendEvent('agent_sessions.sidebar_tab_click', {target: 'CREATED_BY_ME'})
    applyTabParams(getCreatedByMeSearchParams())
  }

  const handleSearchClick = (e: React.MouseEvent<HTMLAnchorElement>, close: () => void) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Preserve native modified-link behavior.
    const hasModifierKey = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey
    if (e.defaultPrevented || e.button !== 0) return
    sendEvent('agent_sessions.sidebar_tab_click', {target: 'SIDEBAR_SEARCH'})
    if (hasModifierKey) return
    e.preventDefault()
    close()
    applyTabParams(getCreatedByMeSearchParams())
  }

  const navigateToNeedsAttention = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    sendEvent('agent_sessions.sidebar_tab_click', {target: 'NEEDS_ATTENTION'})
    const params = new URLSearchParams()
    params.set('q', NEEDS_ATTENTION_QUERY)
    applyTabParams(params)
  }

  const navigateToChat = useCallback(
    (href: string, event: React.MouseEvent<HTMLElement>, trackConversationSelection: boolean) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Preserve native modified-link behavior.
      const hasModifierKey = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
      if (event.defaultPrevented || event.button !== 0 || hasModifierKey) return

      event.preventDefault()
      if (trackConversationSelection) {
        sendEvent('copilot_chat.activate', {
          target: 'CHAT_V2_CONVERSATION_SELECT',
          chat_version: 'v2',
          mode: 'immersive',
        })
      }
      navigate(href)
      onChatNavigate?.()
    },
    [navigate, onChatNavigate],
  )
  const handleChatNavigate = useCallback(
    (href: string, event: React.MouseEvent<HTMLElement>) => navigateToChat(href, event, true),
    [navigateToChat],
  )
  const handleLegacyChatNavigate = useCallback(
    (href: string, event: React.MouseEvent<HTMLElement>) => navigateToChat(href, event, false),
    [navigateToChat],
  )
  const handleNewChatNavigate = useCallback(
    (href: string, event: React.MouseEvent<HTMLElement>) => navigateToChat(href, event, false),
    [navigateToChat],
  )
  const showNewChat = canCreateChat ?? agentsChats !== undefined

  const baseHasTasks =
    groups != null
      ? hasActiveFilter
        ? groups.some(([, groupTasks]) => groupTasks.length > 0)
        : groups.length > 0
      : tasks != null && tasks.length > 0
  // In merged grouping modes, chats can populate the list even with zero tasks.
  const hasAnySessions = isMergedGroupingMode ? baseHasTasks || (sidebarChats?.chats.length ?? 0) > 0 : baseHasTasks
  const hasRenderableSessions = useMemo(() => {
    const renderabilityGroups = effectiveGroups ?? (tasks ? groupTasksByRepo(tasks) : undefined)
    return renderabilityGroups ? hasRenderableGroups(renderabilityGroups, {useGlobalTaskUrls, searchParams}) : false
  }, [effectiveGroups, tasks, useGlobalTaskUrls, searchParams])
  const hasTasks = hasAnySessions && hasRenderableSessions
  const hasPinnedChats = !isMergedGroupingMode && (sidebarChats?.chats.length ?? 0) > 0
  const hasChatSourceError = Boolean(agentsChats?.isErrorChats || legacy.isError)
  const allChatSourcesFailed = Boolean(sidebarChats?.isErrorChats)
  // In merged mode, task failures can own the whole list. Chat failures only do
  // that when v2 is the only enabled chat source.
  // In merged mode, a v2-only chat failure can make isErrorSessions true even though
  // the task list itself never errored; the error state needs its own "chats" wording then.
  const isChatOnlyErrorSessions =
    isMergedGroupingMode &&
    !facetResolutionError &&
    !isErrorTasks &&
    !hasTasks &&
    Boolean(agentsChats?.isErrorChats) &&
    !legacyChatHistoryEnabled
  const isErrorSessions = facetResolutionError
    ? true
    : isMergedGroupingMode
      ? !hasTasks && (isErrorTasks || (Boolean(agentsChats?.isErrorChats) && !legacyChatHistoryEnabled))
      : isErrorTasks
  const isResolvingRepository = Boolean(effectiveSidebarFilters.repo) && !sidebarRepository && !repositoryNotFound
  const isLoadingSessions = Boolean(
    isLoadingTasks ||
    isResolvingRepository ||
    (agentsChats?.isLoadingChats && !agentsChats.isErrorChats && agentsChats.chats.length === 0) ||
    legacy.isLoading ||
    shownPins?.isLoading,
  )
  const isFilteredEmpty =
    hasActiveFilter &&
    !hasTasks &&
    !hasPinnedChats &&
    !repositoryNotFound &&
    !isResolvingRepository &&
    !isErrorSessions &&
    !isLoadingSessions &&
    !sidebarChats?.isLoadingChats &&
    !hasChatSourceError
  const isRepositoryNotFound = showRepositoryNotFound && !facetResolutionError && !hasChatSourceError
  useEffect(() => {
    if (onSidebarStateChange) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent -- Deduplicate announcements across responsive sidebar copies.
      onSidebarStateChange(sidebarId, {
        isLoading: legacy.isLoading,
        isError: legacy.isError,
        isFilteredEmpty,
        isRepositoryNotFound,
      })
    } else {
      if (isFilteredEmpty && !wasFilteredEmptyRef.current)
        announce('No sessions match your filters. Try adjusting your search or filters.')
      if (isRepositoryNotFound && !wasRepositoryNotFoundRef.current) announce('Repository not found.')
    }
    wasFilteredEmptyRef.current = isFilteredEmpty
    wasRepositoryNotFoundRef.current = isRepositoryNotFound
  }, [onSidebarStateChange, sidebarId, legacy.isLoading, legacy.isError, isFilteredEmpty, isRepositoryNotFound])
  useEffect(() => () => onSidebarStateChange?.(sidebarId), [onSidebarStateChange, sidebarId])
  useEffect(() => {
    if (!restoreResetFocusRef.current || isFilteredEmpty || isRepositoryNotFound) return
    restoreResetFocusRef.current = false
    filterButtonRef.current?.focus()
  }, [isFilteredEmpty, isRepositoryNotFound])
  useEffect(() => {
    const pending = olderChatsFocusRef.current
    if (!pending) return
    if (isMergedGroupingMode && isFilteredEmpty && sidebarChats?.hasMoreChats) {
      if (sidebarChats.isLoadingMoreChats) pending.startedLoading = true
      else if (
        pending.startedLoading ||
        sidebarChats.chats.length !== pending.chatCount ||
        sidebarChats.hasLoadMoreError
      ) {
        olderChatsFocusRef.current = null
        if (document.activeElement === document.body && pending.button.isConnected) pending.button.focus()
      }
      return
    }
    olderChatsFocusRef.current = null
    if (document.activeElement !== document.body && document.activeElement !== pending.button) return
    const firstSession = sidebarContainerRef.current?.querySelector<HTMLAnchorElement>(
      '[data-component="NavList.SubNav"]:not([hidden]) a[href]',
    )
    const focusTarget = firstSession ?? filterButtonRef.current
    focusTarget?.focus()
  }, [isMergedGroupingMode, isFilteredEmpty, sidebarChats])
  const handleSessionsClickCapture = useCallback((event: React.MouseEvent<HTMLElement>) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (!event.altKey || !(event.target instanceof Element)) return

    const clickedButton = event.target.closest<HTMLButtonElement>('button')
    if (!clickedButton || clickedButton.matches('[aria-disabled="true"]')) return

    const targetDisclosure = clickedButton.matches('[aria-expanded]')
      ? clickedButton
      : clickedButton.closest('li')?.querySelector<HTMLElement>('[aria-expanded]')
    if (!targetDisclosure?.querySelector('[data-repo-group-heading]')) return

    const shouldOpen = targetDisclosure.getAttribute('aria-expanded') !== 'true'
    const disclosures = targetDisclosure.closest('nav')?.querySelectorAll<HTMLElement>('[aria-expanded]')
    if (!disclosures) return

    // Toggle siblings in place and let the original click toggle its target so hover, focus, and animation stay continuous.
    for (const disclosure of disclosures) {
      if (
        disclosure !== targetDisclosure &&
        disclosure.querySelector('[data-repo-group-heading]') &&
        (disclosure.getAttribute('aria-expanded') === 'true') !== shouldOpen
      ) {
        disclosure.click()
      }
    }

    if (clickedButton !== targetDisclosure) {
      event.preventDefault()
      event.stopPropagation()
      targetDisclosure.click()
      targetDisclosure.focus()
    }
  }, [])

  let repositorySessionsContent: React.ReactNode
  if (isRepositoryNotFound) {
    repositorySessionsContent = (
      <li className={stateStyles.state}>
        <FilterIcon size={24} className={stateStyles.stateIcon} aria-hidden="true" />
        <p className={stateStyles.stateTitle}>Repository not found</p>
        <p className={stateStyles.stateDescription}>Try another repository or reset your filters.</p>
        <Button variant="invisible" size="small" onClick={handleResetEmptyFilters}>
          Reset filters
        </Button>
      </li>
    )
  } else if (isErrorSessions) {
    repositorySessionsContent = (
      <li className={clsx(stateStyles.state, stateStyles.stateError)}>
        <div role="alert">
          <AlertIcon className={stateStyles.stateIcon} />
          <p className={stateStyles.stateTitle}>
            {isChatOnlyErrorSessions
              ? 'Couldn’t load chats'
              : isErrorTasks && allChatSourcesFailed
                ? 'Couldn’t load sessions and chats'
                : 'Couldn’t load sessions'}
          </p>
          <p className={stateStyles.stateDescription}>Check your connection and try again.</p>
        </div>
      </li>
    )
  } else if (hasActiveFilter && !hasTasks && !hasPinnedChats && (isLoadingSessions || sidebarChats?.isLoadingChats)) {
    repositorySessionsContent = <SessionsSkeleton />
  } else if (isFilteredEmpty) {
    repositorySessionsContent = (
      <li className={stateStyles.state}>
        <FilterIcon size={24} className={stateStyles.stateIcon} aria-hidden="true" />
        <p className={stateStyles.stateTitle}>No sessions match your filters</p>
        <p className={stateStyles.stateDescription}>Try adjusting your search or filters</p>
        <Button variant="invisible" size="small" onClick={handleResetEmptyFilters}>
          Reset filters
        </Button>
        {isMergedGroupingMode && sidebarChats?.hasMoreChats && (
          <Button
            variant="invisible"
            size="small"
            onClick={event => {
              if (sidebarChats.isLoadingMoreChats || !sidebarChats.loadMoreChats) return
              olderChatsFocusRef.current = {
                button: event.currentTarget,
                chatCount: sidebarChats.chats.length,
                startedLoading: false,
              }
              sidebarChats.loadMoreChats()
            }}
            aria-disabled={sidebarChats.isLoadingMoreChats || !sidebarChats.loadMoreChats}
          >
            {sidebarChats.isLoadingMoreChats
              ? 'Loading older chats...'
              : sidebarChats.hasLoadMoreError
                ? 'Retry loading older chats'
                : 'Load older chats'}
          </Button>
        )}
      </li>
    )
  } else if (hasTasks) {
    repositorySessionsContent = (
      <SessionsByRepoNav
        tasks={effectiveGroups ? undefined : tasks}
        groups={effectiveGroups}
        activeTaskId={activeTaskId}
        navigateToAgentsPage={navigateToAgentsPage}
        activeChatTaskId={isMergedGroupingMode ? activeChatTaskId : undefined}
        activeThreadId={isMergedGroupingMode ? activeThreadId : undefined}
        chatApiURL={isMergedGroupingMode ? chatApiURL : undefined}
        onNewSession={onPlusButtonClick}
        repositoryAgentEnabledById={repositoryAgentEnabledById}
        getChatStatusLabel={isMergedGroupingMode ? getChatStatusLabel : undefined}
        onChatNavigate={isMergedGroupingMode ? handleChatNavigate : undefined}
        onLegacyChatNavigate={isMergedGroupingMode ? handleLegacyChatNavigate : undefined}
      />
    )
  } else if (isLoadingSessions) {
    repositorySessionsContent = <SessionsSkeleton />
  } else if (hasPinnedChats) {
    repositorySessionsContent = null
  } else if (showSidebarFiltering && hasChatSourceError) {
    repositorySessionsContent = null
  } else {
    repositorySessionsContent = (
      <li className={clsx(stateStyles.state, stateStyles.stateEmpty)}>
        <p className={stateStyles.stateDescription}>Start a new session and it will appear here.</p>
      </li>
    )
  }

  const sessionsContent = isLoadingSessions ? (
    <SessionsSkeleton isLoadingPins={shownPins?.isLoading} />
  ) : (
    <>
      {!isMergedGroupingMode &&
        (agentsChats !== undefined || showNewChat || legacy.isLoading || legacy.isError || legacy.chats.length > 0) && (
          <ChatsNavGroup
            agentsChats={repositoryAgentsChats}
            suppressFullError={isErrorSessions}
            chatApiURL={chatApiURL}
            currentTaskId={activeChatTaskId}
            activeThreadId={activeThreadId}
            getStatusLabel={getChatStatusLabel}
            onNavigate={handleChatNavigate}
            onLegacyNavigate={handleLegacyChatNavigate}
            onNewChat={showNewChat ? handleNewChatNavigate : undefined}
            showNewChat={showNewChat}
          />
        )}
      {isMergedGroupingMode && hasChatSourceError && !isErrorSessions && (
        <li className={clsx(stateStyles.state, stateStyles.stateError, stateStyles.constrained)}>
          <AlertIcon aria-hidden="true" className={stateStyles.stateIcon} />
          <p className={stateStyles.stateTitle}>
            {allChatSourcesFailed ? 'Couldn’t load chats' : 'Couldn’t load some chats'}
          </p>
        </li>
      )}
      {repositorySessionsContent}
    </>
  )

  const onChatTaskStateChange = agentsChats?.onTaskStateChange
  const chatHistory = agentsChats?.chats

  const notifyChatStateChange = useCallback(
    (taskId: string) => {
      const isChat =
        chatHistory?.some(chat => chat.taskId === taskId) ||
        (groups?.flatMap(([, groupTasks]) => groupTasks) ?? tasks)?.some(
          task => task.id === taskId && isQuickChatTask(task),
        )
      if (isChat) onChatTaskStateChange?.(taskId)
    },
    [chatHistory, groups, tasks, onChatTaskStateChange],
  )

  const invalidateGroupedTasks = useCallback(
    (taskId: string) => {
      void queryClient.invalidateQueries({queryKey: agentGroupedTasksQueryKey})
      notifyChatStateChange(taskId)
    },
    [notifyChatStateChange, queryClient],
  )

  const sessionsBody = (
    <TaskActionProviders
      navigateOnDeleteTaskId={activeTaskIdForDeleteRedirect ?? activeChatTaskId}
      chatApiUrl={agentsChats?.apiURL}
      deletionDestination={agentsBasePath}
      onTaskStateChange={invalidateGroupedTasks}
      onDeleteSuccess={notifyChatStateChange}
    >
      <ArchiveErrorBanner />
      <CollapsibleNavList
        aria-label="Sessions"
        className={styles.sessionsNavList}
        onClickCapture={handleSessionsClickCapture}
      >
        {shownPins && !isLoadingSessions && (
          <PinnedSessions
            pins={shownPins}
            activeTaskId={activeTaskId ?? activeChatTaskId}
            onNavigate={onChatNavigate ?? onPlusButtonClick}
          />
        )}
        <CollapsibleNavList.Group
          title="Sessions"
          icon={CollapsedSessionsIcon}
          expandedHeading
          headingLevel="h2"
          showDivider
          popoverWidth="medium"
          className={styles.sessionsListGroup}
          trailingAction={({close}) => (
            <SidebarFilterMenu
              showSidebarFiltering={showSidebarFiltering}
              showSidebarFacetFiltering={showSidebarFacetFiltering}
              showSidebarTypeFacet={showSidebarTypeFacet}
              showSearch={Boolean(currentUserLogin)}
              searchHref={`${agentsBasePath}?${getCreatedByMeSearchParams().toString()}`}
              onSearchClick={event => handleSearchClick(event, close)}
              filterPopoverOpen={filterPopoverOpen}
              onFilterPopoverOpenChange={setFilterPopoverOpen}
              hasActiveSidebarFilter={hasActiveFilter}
              isSidebarExpanded={isSidebarExpanded}
              close={close}
              onPlusButtonClick={onPlusButtonClick}
              navigateToHome={navigateToHome}
              groupBy={effectiveGroupBy}
              onGroupByChange={handleGroupByChange}
              selectedStatuses={effectiveSidebarFilters.statuses}
              onStatusChange={handleStatusChange}
              selectedTypes={effectiveSidebarFilters.types}
              selectedAgents={effectiveSidebarFilters.agents}
              onTypeChange={values => handleFacetChange('types', values)}
              onAgentChange={values => handleFacetChange('agents', values)}
              filterableAgents={filterableAgents}
              repoFilter={sidebarRepository?.nameWithOwner ?? effectiveSidebarFilters.repo}
              onRepoFilterChange={handleRepositoryChange}
              onResetFilters={handleResetFilters}
              filterButtonRef={filterButtonRef}
            />
          )}
        >
          {sessionsContent}
        </CollapsibleNavList.Group>
      </CollapsibleNavList>
    </TaskActionProviders>
  )

  return (
    <div ref={sidebarContainerRef} className={styles.sidebarContainer} onFocusCapture={capturePinFocus}>
      <NavList aria-label="Agent navigation">
        <NavList.Item
          href={agentsBasePath}
          data-task-pins-focus-target
          aria-current={activeThreadId ? undefined : activeView === 'home' ? 'page' : undefined}
          onClick={navigateToHome}
        >
          <NavList.LeadingVisual>
            <HomeIcon />
          </NavList.LeadingVisual>
          Home
        </NavList.Item>
        {currentUserLogin && (
          <NavList.Item
            href={`${agentsBasePath}?${getCreatedByMeSearchParams().toString()}`}
            aria-current={activeView === 'created_by_me' ? 'page' : undefined}
            onClick={navigateToCreatedByMe}
          >
            <NavList.LeadingVisual>
              <PersonIcon />
            </NavList.LeadingVisual>
            My sessions
          </NavList.Item>
        )}
        <NavList.Item
          href={`${agentsBasePath}?${new URLSearchParams({q: NEEDS_ATTENTION_QUERY}).toString()}`}
          aria-current={activeView === 'needs_attention' ? 'page' : undefined}
          onClick={navigateToNeedsAttention}
        >
          <NavList.LeadingVisual>
            <QuestionBubbleIcon />
          </NavList.LeadingVisual>
          Needs attention
          {needsAttentionCount != null && needsAttentionCount > 0 && (
            <NavList.TrailingVisual>
              <CounterLabel>{needsAttentionCountDisplay}</CounterLabel>
            </NavList.TrailingVisual>
          )}
        </NavList.Item>
        {showSpacesNav && (
          <NavList.Item as={SoftNavOnlyLink} to={agentsSpacesPath} aria-current={isSpacesActive ? 'page' : undefined}>
            <NavList.LeadingVisual>
              <SpaceIcon />
            </NavList.LeadingVisual>
            Spaces
          </NavList.Item>
        )}
        {showCustomizeNav && (
          <NavList.Item
            as={SoftNavOnlyLink}
            to={CANONICAL_AGENTS_CUSTOMIZE_PATH}
            aria-current={isCustomizeActive ? 'page' : undefined}
          >
            <NavList.LeadingVisual>
              <ToolsIcon />
            </NavList.LeadingVisual>
            Customize
          </NavList.Item>
        )}
      </NavList>
      {sessionsBody}
    </div>
  )
}

const SKELETON_ROW_WIDTHS = ['95%', '78%', '88%', '65%']

function SessionsSkeleton({isLoadingPins}: {isLoadingPins?: boolean}) {
  return (
    <li className={stateStyles.skeleton} data-task-pins-loading={isLoadingPins || undefined}>
      <div role="status" aria-label="Loading sessions">
        <div className={stateStyles.skeletonHeading} aria-hidden="true">
          <SkeletonText size="bodySmall" maxWidth="30%" />
        </div>
        {SKELETON_ROW_WIDTHS.map(width => (
          <div key={width} className={stateStyles.skeletonRow} aria-hidden="true">
            <SkeletonAvatar size={16} square />
            <SkeletonText maxWidth={width} />
          </div>
        ))}
      </div>
    </li>
  )
}
